// Crisp HUD drawn on a separate full-resolution 2D canvas over the pixel-art world.
import { CONFIG, WEAPONS, CHALLENGES, RARITIES, WORLD, VEHICLES, CYBER, HackId, BOSSES } from '../../common/src/data';
import { GameMap, T, TILE, tileAt } from '../../common/src/map';
import type { Snapshot, PlayerSnap } from '../../common/src/protocol';
import type { Character, ThrowKind } from '../../common/src/rules';
import { THROWABLES, respawnCost, xpForNext, weaponName, weaponSpec } from '../../common/src/rules';
import { HACK_NAMES, describeHack } from '../../common/src/hacks';
import { STATIONS } from './audio';
import type { HackTarget } from './targets';
import { applicable, hackRange } from './targets';
import { hackDuration, type HackLabel } from './hack-fx';
import { crewOf, drawCrewMask } from './crew';
import { buildSchematic, drawFullMap, kioskBadge, C as MAP } from './fullmap';
import { COLOR, FONT, PALETTE } from './theme';
import { mixHex } from './maplayout';
import { paintMenuTexture } from './menu-texture';
import { poiColor } from './city-art';
import { drawPellet } from './smoke-art';
import { drawAbilityIcon } from './ability-icon';

const F = (px: number, bold = false) => `${bold ? FONT.bodyWeightStrong : FONT.bodyWeight} ${px}px ${FONT.body}`;
const COL = {
  teal: COLOR.interactive, pink: COLOR.brand, yellow: COLOR.warning, red: COLOR.danger, white: COLOR.text, acid: COLOR.positive,
  dim: mixHex(COLOR.ground, COLOR.text, 0.55), faint: mixHex(COLOR.ground, COLOR.text, 0.35),
  bg: COLOR.ground + 'b8', edge: COLOR.interactive + '59', track: mixHex(COLOR.ground, COLOR.border, 0.6), shadow: COLOR.ground + 'aa',
  siren: [COLOR.interactive, COLOR.danger] as const,
};

/** The minimap base is cached at this many px per tile, then shrunk smoothly so streets stay lines at the wide view. */
const MINI_PX = 4;
/** World px the corner minimap spans across (was 900; about 1.9x the width, 3.6x the area). */
const MINI_VW = 1700, MINI_VH = MINI_VW * 0.75;

export class Hud {
  mini: HTMLCanvasElement;
  full: HTMLCanvasElement;
  private wasBurned = false; private pulseAt = -1e9;
  constructor(public cv: HTMLCanvasElement, private map: GameMap) {
    this.full = buildSchematic(map);
    paintMenuTexture();
    this.mini = document.createElement('canvas'); this.mini.width = map.w * MINI_PX; this.mini.height = map.h * MINI_PX;
    const g = this.mini.getContext('2d')!;
    const sx = WORLD.districts.find(d => d.id === 'sanctuary')?.x;
    for (let y = 0; y < map.h; y++) for (let x = 0; x < map.w; x++) {
      const t = tileAt(map, x, y);
      const wildT = sx !== undefined && x >= sx; // the sanctuary countryside: road as road, forest dim
      g.fillStyle = wildT && t === T.ALLEY ? MAP.road : wildT && t === T.BOLLARD ? MAP.forest : t === T.ROAD ? MAP.road : t === T.SIDEWALK ? MAP.sidewalk : t === T.ALLEY || t === T.BOLLARD ? MAP.alley : t === T.PLAZA ? MAP.plaza
        : t === T.BUILDING ? COLOR.ground : t === T.RAIL ? MAP.rail : t === T.LOW || t === T.THIN ? MAP.low : mixHex(MAP.block, PALETTE.amber, 0.35);
      g.fillRect(x * MINI_PX, y * MINI_PX, MINI_PX, MINI_PX);
    }
    // the Kanemori Sanctuary strip: a faint cyan wash, so its mountain reads as its own place beside Okubo's blocks
    const sd = WORLD.districts.find(d => d.id === 'sanctuary');
    if (sd) { g.fillStyle = PALETTE.cyan + '14'; g.fillRect(sd.x * MINI_PX, sd.y * MINI_PX, sd.w * MINI_PX, sd.h * MINI_PX); }
  }
  draw(o: {
    s: Snapshot; me: PlayerSnap; char: Character; scale: number; w2s: (x: number, y: number) => [number, number];
    mouse: [number, number]; hackMode: boolean; hackTarget: HackTarget | null; driverTarget: HackTarget | null; players: PlayerSnap[];
    objective: { text: string; x: number; y: number; boss?: boolean } | null; fps: number; rtt: number; radio: number; showMap: boolean; scanner: boolean; npcsVisible: { x: number; y: number; k: string; st: number }[]; clubBoss?: { x: number; y: number; hp: number; mhp: number; n: string };
    /** Shingan and his decoys (M10): his name and health over each, so the bar doesn't give the real one away */
    abbot?: { x: number; y: number; hp: number; mhp: number; n: string; top: number }[];
    storyPin?: { x: number; y: number } | null;
    goal?: { text: string; target: { x: number; y: number } | null; label?: string; pointLabel?: string } | null;
    hackFx?: boolean; hackLabels?: HackLabel[];
    lastErr: string; touch?: boolean; throwable?: { kind: string; short: string; count: number }; lowFx?: boolean; characterHeight?: number;
  }) {
    const cv = this.cv, g = cv.getContext('2d')!;
    const S = o.scale; const W = cv.width, H = cv.height;
    g.clearRect(0, 0, W, H);
    const u = Math.max(1, Math.min(W / 640, H / 360)); // UI unit
    const me = o.me;
    const headRise = (p: PlayerSnap) => !p.v && !p.d && o.characterHeight ? o.characterHeight - 4 : 12;
    g.textBaseline = 'top';
    const panel = (x: number, y: number, w: number, h: number) => { g.fillStyle = COL.bg; g.fillRect(x, y, w, h); g.strokeStyle = COL.edge; g.lineWidth = 1; g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1); };
    const bar = (x: number, y: number, w: number, h: number, f: number, col: string, bg: string = COL.track) => { g.fillStyle = bg; g.fillRect(x, y, w, h); g.fillStyle = col; g.fillRect(x, y, w * Math.max(0, Math.min(1, f)), h); };
    const text = (t: string, x: number, y: number, col: string = COL.white, size = 7, bold = false, align: CanvasTextAlign = 'left') => { g.font = F(Math.max(11, size * u * 1.25), bold); g.textAlign = align; g.fillStyle = COL.shadow; g.fillText(t, x + u * 0.6, y + u * 0.6); g.fillStyle = col; g.fillText(t, x, y); };

    // "you are here" chevron so the local player never gets lost in the scenery
    {
      const [x, y] = o.w2s(me.x, me.y); const bob = Math.sin(Date.now() / 180) * u;
      const ty = y - (me.v ? 16 : headRise(me)) * S - 4 * u + bob;
      g.fillStyle = COLOR.ground; g.beginPath(); g.moveTo(x - 6 * u, ty - 5 * u); g.lineTo(x + 6 * u, ty - 5 * u); g.lineTo(x, ty + 2 * u); g.fill();
      g.fillStyle = crewOf(o.players, me.id).color; g.beginPath(); g.moveTo(x - 4.5 * u, ty - 4 * u); g.lineTo(x + 4.5 * u, ty - 4 * u); g.lineTo(x, ty + 0.5 * u); g.fill();
      if (me.fx & 16) text('+ RECOVERING', x, y + 13 * S, COL.teal, 6, true, 'center');
      else if (me.fx & 32) text('RECOVERY PAUSED: wanted or just hurt', x, y + 13 * S, COL.dim, 5, false, 'center');
    }
    // world-space labels: teammates' names, downed markers, pings, objective
    for (const p of o.players) {
      if (p.id === me.id || (p.fx & 4)) continue;
      const [x, y] = o.w2s(p.x, p.y);
      const cm = crewOf(o.players, p.id);
      if (x < 0 || y < 0 || x > W || y > H) { // off screen: their mask on the screen edge with an arrow pointing their way
        const cx = W / 2, cy = H / 2, a = Math.atan2(y - cy, x - cx), m = 22 * u;
        const k = Math.min((cx - m) / Math.max(1e-3, Math.abs(Math.cos(a))), (cy - m) / Math.max(1e-3, Math.abs(Math.sin(a))));
        const ex = cx + Math.cos(a) * k, ey = cy + Math.sin(a) * k;
        g.fillStyle = COLOR.ground; g.beginPath(); g.arc(ex, ey, 9 * u, 0, 6.3); g.fill();
        g.strokeStyle = cm.color; g.lineWidth = 2; g.stroke();
        drawCrewMask(g, o.players, p.id, ex, ey, 12 * u);
        g.save(); g.translate(ex + Math.cos(a) * 14 * u, ey + Math.sin(a) * 14 * u); g.rotate(a);
        g.fillStyle = cm.color; g.beginPath(); g.moveTo(5 * u, 0); g.lineTo(-3 * u, -5 * u); g.lineTo(-3 * u, 5 * u); g.fill(); g.restore();
        continue;
      }
      text(p.n + (p.fx & 8 ? ' [TEST]' : ''), x, y - (o.characterHeight && !p.v && !p.d ? headRise(p) + 10 : 16) * S, cm.color, 6, false, 'center');
      if (p.d) text(`DOWN ${p.dt}s  ${o.touch ? 'USE' : '[E]'} revive`, x, y + 9 * S, COL.red, 6, true, 'center');
      else if (o.char.cyber.installed.includes('kizuna_link')) { bar(x - 10 * S, y - (o.characterHeight && !p.v ? headRise(p) + 2 : 12) * S, 20 * S, 2 * u, p.hp / p.mhp, p.hp / p.mhp < 0.3 ? COL.red : COL.pink); } // Kizuna Link: crew health over their heads
    }
    if (me.d === 0) for (const p of o.players) if (p.d && p.id !== me.id) { /* already labelled */ }
    for (const pg of o.s.pg) {
      const [x, y] = o.w2s(pg.x, pg.y); const col = pg.k === 'enemy' ? COL.red : pg.k === 'object' ? COL.yellow : COL.teal;
      g.strokeStyle = col; g.lineWidth = 2; g.beginPath(); g.arc(x, y, (6 + Math.sin(Date.now() / 150) * 2) * S, 0, 6.3); g.stroke();
      const who = o.players.find(p => p.id === pg.by)?.n ?? '';
      text(`${pg.k.toUpperCase()} · ${who}`, x, y + 10 * S, col, 5, false, 'center');
    }
    for (const ab of o.abbot ?? []) {
      const [x, y] = o.w2s(ab.x, ab.y);
      // 	op is how far above his feet the label starts: the taller art sprite needs more, and its labels are a little smaller so three of them don't pile up
      const tall = ab.top > 28;
      text(ab.n.toUpperCase(), x, y - ab.top * S, COL.white, tall ? 5 : 6, true, 'center');
      bar(x - (tall ? 11 : 14) * S, y - (ab.top - 8) * S, (tall ? 22 : 28) * S, 3 * u, ab.hp / ab.mhp, COL.red);
    }
    if (o.clubBoss) { // Kumadori Club boss: name and health over his head (name comes from the snapshot)
      const [x, y] = o.w2s(o.clubBoss.x, o.clubBoss.y);
      text(o.clubBoss.n.toUpperCase(), x, y - 28 * S, COL.white, 6, true, 'center');
      bar(x - 14 * S, y - 20 * S, 28 * S, 3 * u, o.clubBoss.hp / o.clubBoss.mhp, COL.red);
    }
    if (o.scanner) for (const n of o.npcsVisible) if (n.k !== 'ped') {
      const [x, y] = o.w2s(n.x, n.y); text(n.k.toUpperCase(), x, y - (o.characterHeight && n.k !== 'drone' ? o.characterHeight + 6 : 14) * S, n.st === 2 ? COL.red : COL.dim, 5, false, 'center');
    }
    // hacking labels (hack-fx.ts): lock-on name, real seconds left on a hacked object, refusal reasons; drawn on the world, small, centred
    for (const l of o.hackLabels ?? []) {
      const [x, y] = o.w2s(l.x, l.y); if (x < 0 || y < 0 || x > W || y > H) continue;
      text(l.text, x, y - 3 * u, l.tone === 'danger' ? COL.red : l.tone === 'select' ? COL.white : l.tone === 'dim' ? COL.dim : COL.teal, 5, l.tone !== 'dim', 'center');
    }
    if (o.objective) {
      const [x, y] = o.w2s(o.objective.x, o.objective.y);
      const inView = x > 0 && y > 0 && x < W && y < H;
      if (inView && o.objective.boss) { /* the boss is its own marker */ }
      else if (inView) { g.strokeStyle = COL.yellow; g.lineWidth = 2; g.strokeRect(x - 9 * S, y - 9 * S, 18 * S, 18 * S); text('OBJECTIVE', x, y - 18 * S, COL.yellow, 6, true, 'center'); }
      else { // edge arrow
        const cx = W / 2, cy = H / 2, a = Math.atan2(y - cy, x - cx);
        const ex = cx + Math.cos(a) * (Math.min(W, H) / 2 - 30 * u), ey = cy + Math.sin(a) * (Math.min(W, H) / 2 - 30 * u);
        g.save(); g.translate(ex, ey); g.rotate(a); g.fillStyle = COL.yellow; g.beginPath(); g.moveTo(10 * u, 0); g.lineTo(-6 * u, -6 * u); g.lineTo(-6 * u, 6 * u); g.fill(); g.restore();
      }
    }

    // top-left: vitals
    const x0 = 6 * u, y0 = 6 * u;
    panel(x0, y0, 132 * u, 52 * u);
    text(`${o.char.name}  LV${o.char.level}`, x0 + 4 * u, y0 + 3 * u, COL.teal, 7, true);
    bar(x0 + 4 * u, y0 + 13 * u, 124 * u, 6 * u, me.hp / me.mhp, me.hp / me.mhp < 0.3 ? COL.red : COL.pink);
    text(`HP ${me.hp}/${me.mhp}`, x0 + 6 * u, y0 + 13 * u, COL.white, 5);
    if (me.ar > 0) { bar(x0 + 4 * u, y0 + 20 * u, 124 * u, 2 * u, me.ar / 30, COL.white); }
    for (let i = 0; i < me.mram; i++) { g.fillStyle = i < Math.floor(me.ram) ? COL.acid : COL.track; g.fillRect(x0 + 4 * u + i * 9 * u, y0 + 24 * u, 7 * u, 5 * u); }
    text(`RAM ${me.ram.toFixed(1)}/${me.mram}`, x0 + 4 * u, y0 + 31 * u, COL.acid, 5);
    const act = o.char.cyber.active;
    const abText = act ? `${CYBER.items[act].name.split(' ').slice(-2).join(' ')} ${me.ab > 0 ? me.ab.toFixed(1) + 's' : (o.touch ? 'READY · CYBER' : 'READY [Space]')}` : 'No active cyberware';
    bar(x0 + 4 * u, y0 + 40 * u, 124 * u, 3 * u, me.abm ? 1 - me.ab / me.abm : 1, me.ab > 0 ? COL.dim : COL.teal);
    const abCol = me.ab > 0 ? COL.dim : COL.teal;
    if (act) drawAbilityIcon(g, act, x0 + 4 * u, y0 + 44 * u, 7 * u, abCol);
    text(abText, x0 + (act ? 12 : 4) * u, y0 + 44 * u, abCol, 5);
    // teammates
    let ty = y0 + 56 * u;
    const kizuna = o.char.cyber.installed.includes('kizuna_link');
    for (const p of o.players) {
      if (p.id === me.id) continue;
      const cm = crewOf(o.players, p.id);
      panel(x0, ty, 100 * u, 14 * u);
      g.fillStyle = cm.color; g.fillRect(x0, ty, 2 * u, 14 * u);
      drawCrewMask(g, o.players, p.id, x0 + 9 * u, ty + 7 * u, 11 * u);
      text(`${p.n}${p.fx & 4 ? ' (reconnecting)' : ''}`, x0 + 16 * u, ty + 1 * u, p.d ? COL.red : cm.color, 5);
      bar(x0 + 16 * u, ty + 9 * u, 81 * u, 3 * u, p.hp / p.mhp, p.d ? COL.red : COL.pink);
      if (kizuna && !p.d) text(`${p.hp}/${p.mhp}`, x0 + 76 * u, ty + 1 * u, COL.white, 5); // Kizuna Link: exact crew health
      if (p.d) text(`DOWN ${p.dt}s`, x0 + 60 * u, ty + 1 * u, COL.red, 5, true);
      ty += 16 * u;
    }
    // top-right: heat, money, cred
    const xr = W - 6 * u;
    panel(xr - 150 * u, y0, 150 * u, 40 * u);
    for (let i = 0; i < 5; i++) {
      g.fillStyle = i < me.h ? (me.ph === 1 ? (Math.floor(Date.now() / 250) % 2 ? COL.red : COL.yellow) : COL.yellow) : COL.track;
      const sx = xr - 146 * u + i * 13 * u, sy = y0 + 4 * u;
      g.beginPath(); for (let k = 0; k < 10; k++) { const r = k % 2 ? 2.5 * u : 5.5 * u; const a = -Math.PI / 2 + k * Math.PI / 5; g.lineTo(sx + 5 * u + Math.cos(a) * r, sy + 5 * u + Math.sin(a) * r); } g.fill();
    }
    const phase = me.ph === 1 ? 'CHASE' : me.ph === 2 ? `SEARCH ${me.st}s` : me.h ? '' : 'CLEAN';
    text(phase, xr - 78 * u, y0 + 4 * u, me.ph === 1 ? COL.red : me.ph === 2 ? COL.yellow : COL.teal, 7, true);
    text(`$${o.char.money}`, xr - 146 * u, y0 + 17 * u, COL.yellow, 8, true);
    text(`CRED ${o.char.cred}`, xr - 80 * u, y0 + 17 * u, COL.pink, 6, true);
    bar(xr - 146 * u, y0 + 30 * u, 142 * u, 2 * u, o.char.level >= 10 ? 1 : o.char.xp / xpForNext(o.char.level), COL.teal);
    if (me.cc > 0) text(`CRIME CHAIN x${me.cc}`, xr - 146 * u, y0 + 33 * u, COL.pink, 5, true);
    // Kaminari-gumi burn: word + timer under the heat panel; pink pulse once when the burn starts
    const burned = (me.bu ?? 0) > 0;
    if (burned && !this.wasBurned && !o.lowFx && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.pulseAt = performance.now();
    this.wasBurned = burned;
    let oy = 44 * u;
    if (burned) {
      const bu = me.bu!;
      panel(xr - 150 * u, y0 + oy, 150 * u, 13 * u);
      text(`雷 BURNED ${Math.floor(bu / 60)}:${String(bu % 60).padStart(2, '0')}`, xr - 146 * u, y0 + oy + 2 * u, COL.pink, 6, true);
      oy += 15 * u;
    }
    const pulse = (performance.now() - this.pulseAt) / 700;
    if (pulse >= 0 && pulse < 1) {
      const a = Math.sin(pulse * Math.PI), m = Math.min(W, H) * 0.4;
      for (const [x1, y1, x2, y2, rx, ry, rw, rh] of [[0, 0, m, 0, 0, 0, m, H], [W, 0, W - m, 0, W - m, 0, m, H], [0, 0, 0, m, 0, 0, W, m], [0, H, 0, H - m, 0, H - m, W, m]] as number[][]) {
        const gr = g.createLinearGradient(x1, y1, x2, y2); gr.addColorStop(0, COL.pink); gr.addColorStop(1, COL.pink + '00');
        g.globalAlpha = a * 0.75; g.fillStyle = gr; g.fillRect(rx, ry, rw, rh);
      }
      g.globalAlpha = 1;
    }
    // objective panel
    const myCh = o.s.c.find(c => c.parts.includes(me.id));
    if (myCh) {
      panel(xr - 150 * u, y0 + oy, 150 * u, 22 * u);
      text(`${myCh.hard ? 'HARD ' : ''}${CHALLENGES[myCh.k].name}  ${myCh.tl}s`, xr - 146 * u, y0 + oy + 3 * u, COL.yellow, 6, true);
      text(o.objective?.text ?? '', xr - 146 * u, y0 + oy + 12 * u, COL.white, 5);
    } else if (o.s.c.length) {
      panel(xr - 150 * u, y0 + oy, 150 * u, 12 * u);
      text(`${o.s.c.length} crew job(s) active — join via [Tab] Jobs`, xr - 146 * u, y0 + oy + 3 * u, COL.dim, 5);
      oy += 14 * u;
    }
    if (myCh) oy += 24 * u;
    // next goal: one wrapped hint under the heat panel (top-right is clear of the chat feed and tutorial card)
    if (o.goal) {
      const fs = Math.max(11, 5 * u * 1.25), lh = fs + 2, tw = 138 * u;
      g.font = F(fs, false);
      const words = `${o.goal.label ?? 'NEXT'} · ${o.goal.text}`.split(' '), lines: string[] = [];
      let cur = '';
      for (const w of words) { const t = cur ? `${cur} ${w}` : w; if (cur && g.measureText(t).width > tw) { lines.push(cur); cur = w; } else cur = t; }
      lines.push(cur);
      panel(xr - 150 * u, y0 + oy, 150 * u, lines.length * lh + 6 * u);
      lines.forEach((l, i) => text(l, xr - 146 * u, y0 + oy + 3 * u + i * lh, i === 0 ? COL.yellow : COL.white, 5));
      // pointer: an edge arrow toward the nearest kiosk when it is off-screen
      const tg = o.goal.target;
      if (tg) {
        const [sx, sy] = o.w2s(tg.x, tg.y), m = 18 * u;
        if (sx < 0 || sy < 0 || sx > W || sy > H) {
          const cx = W / 2, cy = H / 2, a = Math.atan2(sy - cy, sx - cx), k = Math.min((W / 2 - m) / Math.max(1e-3, Math.abs(Math.cos(a))), (H / 2 - m) / Math.max(1e-3, Math.abs(Math.sin(a))));
          let ax = cx + Math.cos(a) * k, ay = cy + Math.sin(a) * k;
          // keep the arrow and its label out of the HUD panels: slide it along the same screen edge to the nearest free spot
          const mmW = (o.touch ? 80 : 128) * u, mmH = (o.touch ? 60 : 96) * u, pad = 16 * u;
          const busy: [number, number, number, number][] = [
            [W - mmW - 6 * u, o.touch ? 70 * u : H - mmH - 6 * u, W, o.touch ? 70 * u + mmH + 6 * u : H], // minimap
            [xr - 152 * u, 0, W, 84 * u], // heat and goal panels
            [0, 0, 142 * u, 60 * u], // status panel
            [W / 2 - 92 * u, H - 30 * u, W / 2 + 160 * u, H], // weapon and throwable bar
          ];
          const hit = (px: number, py: number) => busy.some(([l, t, r, b]) => px > l - pad && px < r + pad && py > t - pad && py < b + pad);
          if (hit(ax, ay)) {
            const alongX = (H / 2 - m) / Math.max(1e-3, Math.abs(Math.sin(a))) <= (W / 2 - m) / Math.max(1e-3, Math.abs(Math.cos(a))); // top or bottom edge
            const lo = m, hi = (alongX ? W : H) - m, start = alongX ? ax : ay;
            for (let d = 4; d < Math.max(W, H); d += 4) {
              const free = [start - d, start + d].filter(v => v >= lo && v <= hi && !(alongX ? hit(v, ay) : hit(ax, v)));
              if (free.length) { const v = free.reduce((p, q) => (Math.abs(p - start) <= Math.abs(q - start) ? p : q)); if (alongX) ax = v; else ay = v; break; }
            }
          }
          const pulse = 0.65 + 0.35 * Math.sin(Date.now() / 250);
          g.save(); g.translate(ax, ay); g.rotate(a); g.globalAlpha = pulse;
          g.fillStyle = COLOR.ground; g.beginPath(); g.moveTo(9 * u, 0); g.lineTo(-6 * u, -7 * u); g.lineTo(-6 * u, 7 * u); g.fill();
          g.fillStyle = COL.yellow; g.beginPath(); g.moveTo(7 * u, 0); g.lineTo(-4.5 * u, -5 * u); g.lineTo(-4.5 * u, 5 * u); g.fill();
          g.restore();
          text(o.goal.pointLabel ?? 'KIOSK', ax, ay + (ay > cy ? -20 * u : 10 * u), COL.yellow, 5, true, 'center');
        } else if (o.goal.label === 'STORY') { // story mode: a marker over the target while it is on screen
          const pulse = Math.sin(Date.now() / 250) * 1.5 * u, by = sy - 16 * u + pulse;
          g.fillStyle = COLOR.ground; g.beginPath(); g.moveTo(sx, by + 7 * u); g.lineTo(sx - 6 * u, by - 1 * u); g.lineTo(sx + 6 * u, by - 1 * u); g.fill();
          g.fillStyle = COL.yellow; g.beginPath(); g.moveTo(sx, by + 5.5 * u); g.lineTo(sx - 4.5 * u, by + 0); g.lineTo(sx + 4.5 * u, by + 0); g.fill();
          text('STORY', sx, by - 8 * u, COL.yellow, 5, true, 'center');
        }
      }
    }
    // top-centre: boss health bar (everyone in the room sees it), or its arrival countdown
    const boss = o.s.b?.[0];
    if (boss) {
      const spec = BOSSES.tiers[boss.k], bw = 220 * u, bx = W / 2 - bw / 2, by = y0;
      panel(bx, by, bw, 24 * u);
      if (boss.w > 0) text(`INCOMING: ${spec.name} in ${boss.w}s`, W / 2, by + 7 * u, COL.red, 7, true, 'center');
      else {
        text(`${spec.name}${boss.ph ? `  · ENRAGED${boss.ph > 1 ? ' x2' : ''}` : ''}`, W / 2, by + 2 * u, boss.ph ? COL.red : COL.yellow, 6, true, 'center');
        bar(bx + 4 * u, by + 14 * u, bw - 8 * u, 6 * u, boss.hp / boss.mhp, COL.red);
        for (const f of spec.phases) { g.fillStyle = COL.white; g.fillRect(bx + 4 * u + (bw - 8 * u) * f, by + 13 * u, 1 * u, 8 * u); }
      }
    }
    // bottom-centre: weapon + context hints
    const held = me.sl > 0 ? o.char.inventory.find((i: any) => i.uid === o.char.equipped[me.sl - 1]) ?? null : null;
    const broken = !!held && weaponSpec(held).ok === false;
    const rt = RARITIES.tiers[me.wr as keyof typeof RARITIES.tiers];
    panel(W / 2 - 90 * u, H - 30 * u, 180 * u, 24 * u);
    const blade = WEAPONS[me.w as 'fists']?.kind === 'melee'; // the katana or a tanto: no magazine (throwables have their own panel)
    text(me.w === 'fists' ? weaponName(me.w).toUpperCase() : `${rt ? rt.name + ' ' : ''}${weaponName(me.w)}`, W / 2 - 86 * u, H - 28 * u, rt?.color ?? COL.white, 6, true);
    // selected throwable (G throws, T switches), beside the weapon panel
    if (o.throwable && !me.v) {
      const th = o.throwable, tx = W / 2 + 94 * u, empty = th.count <= 0;
      panel(tx, H - 30 * u, 62 * u, 24 * u);
      if (th.kind === 'smoke') drawPellet(g, tx + 11 * u, H - 15 * u, 5 * u);
      else if (th.kind === 'emp') { g.strokeStyle = empty ? COL.dim : COL.teal; g.lineWidth = 1.6 * u; g.beginPath(); g.moveTo(tx + 6 * u, H - 13 * u); g.lineTo(tx + 16 * u, H - 23 * u); g.stroke(); g.lineWidth = 1 * u; g.beginPath(); g.moveTo(tx + 9 * u, H - 22 * u); g.lineTo(tx + 13 * u, H - 18 * u); g.lineTo(tx + 10 * u, H - 17 * u); g.lineTo(tx + 14 * u, H - 13 * u); g.stroke(); }
      else { g.strokeStyle = empty ? COL.dim : COL.white; g.lineWidth = 1.6 * u; g.beginPath(); for (let i = 0; i < 4; i++) { const a = Math.PI / 4 + i * Math.PI / 2; g.moveTo(tx + 11 * u, H - 18 * u); g.lineTo(tx + 11 * u + Math.cos(a) * 5 * u, H - 18 * u + Math.sin(a) * 5 * u); } g.stroke(); }
      text(`${th.count}`, tx + 58 * u, H - 28 * u, empty ? COL.dim : COL.white, 7, true, 'right');
      text(th.short, tx + 22 * u, H - 28 * u, COL.dim, 4);
      text(o.touch ? '' : '[G] throw [T]', tx + 22 * u, H - 17 * u, COL.teal, 4);
    }
    text(blade ? 'melee' : broken ? 'NEEDS PARTS' : me.rel ? 'RELOADING…' : `${me.mag} / ${me.res}`, W / 2 + 86 * u, H - 28 * u, broken ? COL.red : me.rel ? COL.yellow : COL.white, 7, true, 'right');
    if (me.ch > 0) bar(W / 2 - 86 * u, H - 18 * u, 172 * u, 3 * u, me.ch, me.ch >= 1 ? COL.teal : COL.dim);
    const veh = me.v ? o.s.v.find(v => v.id === me.v) : null;
    if (veh) text(`${VEHICLES[veh.t].name}  ${Math.round(veh.hp / veh.mhp * 100)}%  ·  radio: ${STATIONS[veh.rad]?.name ?? 'OFF'} [N]`, W / 2, H - 17 * u, COL.dim, 5, false, "center");
    // driver hack hint
    if (o.driverTarget) {
      const h = applicable(o.driverTarget)[0] as HackId;
      const cfg = CONFIG.hacks[h];
      const ok = me.ram >= cfg.ram && !(me.hk[h] > 0);
      panel(W / 2 - 120 * u, H - 62 * u, 240 * u, 28 * u);
      text(`${o.touch ? 'HACK' : '[Q]'} ${HACK_NAMES[h]} → ${o.driverTarget.label}`, W / 2, H - 59 * u, ok ? COL.acid : COL.dim, 7, true, 'center');
      text(`${describeHack(h, o.driverTarget.kind, o.driverTarget.raised)} · ${cfg.ram} RAM${me.hk[h] > 0 ? ` · cooldown ${me.hk[h].toFixed(1)}s` : ''}`, W / 2, H - 47 * u, COL.white, 5, false, 'center');
    }
    // hack mode panel (hackfx): one fixed spot in the right column under the goal line (top centre on touch), so it never
    // covers the street, the target or the chat feed; no target name here (the world label carries it), one line per applicable hack
    if (o.hackMode && o.hackFx) {
      const t = o.hackTarget, list = t ? applicable(t) : [], pw = 150 * u, ph = (t ? 13 + list.length * 9 : 12) * u;
      const px = o.touch ? (W - pw) / 2 : W - pw - 6 * u, py = (o.touch ? 42 : 66) * u; // touch: the right column is under the buttons, so top centre below MENU/MAP
      panel(px, py, pw, ph);
      text(t ? 'QUICKHACK' : o.touch ? 'QUICKHACK · tap a target' : 'QUICKHACK · hover a target', px + 4 * u, py + 3 * u, COL.teal, 5, true);
      list.forEach((h, i) => {
        const cfg = CONFIG.hacks[h] as any, d = Math.hypot(t!.x - me.x, t!.y - me.y), res = o.s.inf.res[t!.key] ?? 0;
        const why = res > 0 ? `busy ${Math.ceil(res)}s` : d > hackRange(h) ? 'out of range' : me.ram < cfg.ram ? 'low RAM' : me.hk[h] > 0 ? `cooldown ${me.hk[h].toFixed(1)}s` : 'READY';
        const dur = hackDuration(h, t!.kind);
        text(`${o.touch ? '' : `${['short_circuit', 'sensor_jam', 'remote_override'].indexOf(h) + 1} `}${HACK_NAMES[h]}  ${cfg.ram} RAM  ${dur ? `${dur}s` : 'instant'}  ${why}`, px + 4 * u, py + 12 * u + i * 9 * u, why === 'READY' ? COL.acid : COL.dim, 5);
      });    } else if (o.hackMode) {
      panel(W / 2 - 130 * u, 70 * u, 260 * u, o.hackTarget ? 60 * u : 20 * u);
      text(o.touch ? 'QUICKHACK — world keeps moving. Tap a ◆ target, then a hack button. HACK closes' : 'QUICKHACK — world keeps moving. Hover a ◆ target, press 1/2/3. [Q] or shoot to close', W / 2, 73 * u, COL.acid, 5, true, 'center');
      if (o.hackTarget) {
        text(o.hackTarget.label, W / 2, 83 * u, COL.yellow, 6, true, 'center');
        const hs: HackId[] = ['short_circuit', 'sensor_jam', 'remote_override'];
        hs.forEach((h, i) => {
          const can = applicable(o.hackTarget!).includes(h); const cfg = CONFIG.hacks[h] as any;
          const d = Math.hypot(o.hackTarget!.x - me.x, o.hackTarget!.y - me.y);
          const dur = h === 'short_circuit' ? (o.hackTarget!.kind === 'fix' ? cfg.hazardDuration : 0) : h === 'sensor_jam' ? cfg.duration : o.hackTarget!.kind === 'vent' ? CONFIG.vent.duration : o.hackTarget!.kind === 'sig' ? cfg.signalDuration : o.hackTarget!.kind === 'bar' ? cfg.barrierDuration : cfg.vehicleDuration;
          const why = !can ? 'n/a' : d > hackRange(h) ? 'out of range' : me.ram < cfg.ram ? 'low RAM' : me.hk[h] > 0 ? `cd ${me.hk[h].toFixed(1)}s` : 'READY';
          const col = !can ? COL.faint : why === 'READY' ? COL.acid : COL.dim;
          text(`${i + 1} ${HACK_NAMES[h]}  ${cfg.ram} RAM  ${dur ? dur + 's' : 'instant'}  cd ${cfg.cooldown}s  — ${can ? describeHack(h, o.hackTarget!.kind, o.hackTarget!.raised) : ''}  [${why}]`, W / 2 - 124 * u, 93 * u + i * 10 * u, col, 5);
        });
      }
    }
    // minimap
    // on touch screens the bottom-right belongs to the right thumb, so the minimap moves top-right
    const mw = (o.touch ? 80 : 128) * u, mh = (o.touch ? 60 : 96) * u, mx = W - mw - 6 * u, my = o.touch ? 70 * u : H - mh - 6 * u;
    this.drawMap(g, mx, my, mw, mh, o);
    if (o.showMap) drawFullMap(g, this.full, { s: o.s, me, players: o.players, objective: o.objective, story: o.storyPin ?? null, w: W, h: H, u, mapW: this.map.w * TILE, mapH: this.map.h * TILE });
    // downed overlay
    if (me.d) {
      g.fillStyle = COLOR.danger + '30'; g.fillRect(0, 0, W, H);
      text(`YOU ARE DOWN — ${me.dt}s`, W / 2, H / 2 - 30 * u, COL.red, 14, true, 'center');
      text(me.rs > 0 ? `Being revived… ${Math.round(me.rs * 100)}%` : 'A teammate can revive you (E). Or respawn now:', W / 2, H / 2 - 8 * u, COL.white, 7, false, 'center');
      text(`[Enter] Respawn at Doc Neko's clinic (-$${Math.min(respawnCost(o.char, me.h > 0), o.char.money)}${me.h > 0 ? ' while wanted' : ''}, gear kept)`, W / 2, H / 2 + 4 * u, COL.teal, 7, true, 'center');
    }
    // crosshair
    const [cx, cy] = o.mouse;
    g.strokeStyle = o.hackMode ? COL.acid : COL.pink; g.lineWidth = Math.max(1, u * 0.8);
    // recoil bloom opens the crosshair by the extra cone at the cursor's distance
    const [px, py] = o.w2s(me.x, me.y); const gap = Math.min(14 * u, (me.bl ?? 0) * Math.hypot(cx - px, cy - py));
    const a0 = 2 * u + gap, a1 = 6 * u + gap;
    g.beginPath(); g.moveTo(cx - a1, cy); g.lineTo(cx - a0, cy); g.moveTo(cx + a0, cy); g.lineTo(cx + a1, cy); g.moveTo(cx, cy - a1); g.lineTo(cx, cy - a0); g.moveTo(cx, cy + a0); g.lineTo(cx, cy + a1); g.stroke();
    text(`${o.fps} fps · ${o.rtt} ms`, 6 * u, H - 10 * u, COL.faint, 5);
    if (o.lastErr) text(o.lastErr, W / 2, 60 * u, COL.red, 6, true, 'center');
  }
  /** The corner minimap: a window of the city around you. The full map is `drawFullMap` (fullmap.ts). */
  private drawMap(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, o: any) {
    const s = o.s as Snapshot; const me = o.me as PlayerSnap;
    g.save();
    g.textAlign = 'left'; g.textBaseline = 'top'; // labels start at their point (other HUD text leaves these centred)
    g.fillStyle = COLOR.ground + 'd9'; g.fillRect(x - 2, y - 2, w + 4, h + 4);
    g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
    const mapW = this.map.w * TILE, mapH = this.map.h * TILE;
    const vw = MINI_VW, vh = MINI_VH, vx = Math.max(0, Math.min(mapW - vw, me.x - vw / 2)), vy = Math.max(0, Math.min(mapH - vh, me.y - vh / 2));
    g.beginPath(); g.rect(x, y, w, h); g.clip();
    const k = MINI_PX / TILE; g.drawImage(this.mini, vx * k, vy * k, vw * k, vh * k, x, y, w, h);
    const P = (px: number, py: number): [number, number] => [x + (px - vx) / vw * w, y + (py - vy) / vh * h];
    const dot = (px: number, py: number, col: string, r: number) => { const [a, b] = P(px, py); g.fillStyle = col; g.fillRect(a - r, b - r, r * 2, r * 2); };
    const u = w / 128;
    // the same colors as the shops' reach squares on the map (one list, city-art.ts)
    for (const poi of WORLD.pois) if (poi.kind !== 'kiosk') dot(poi.x * TILE, poi.y * TILE, poiColor(poi.kind), 2 * u);
    for (const poi of WORLD.pois) if (poi.kind === 'kiosk') { // fixer kiosks (jobs + ammo): the full map's pink diamond
      const [a, b] = P(poi.x * TILE, poi.y * TILE); g.save(); g.translate(a, b);
      kioskBadge(g, 4.2 * u, `${Math.max(7, 4.4 * u)}px ${FONT.display}`); g.restore();
    }
    if (s.cm) for (const c of WORLD.cameras) dot(c.x * TILE, c.y * TILE, COL.red, 1.4 * u);
    // Tail Job: stay inside this ring around the target or you lose it (the mirror cone is drawn on the ground, render.ts)
    const tail = s.c?.find(j => j.k === 'tail' && j.parts.includes(me.id)), tv = tail && s.v.find(v => v.id === tail.tv);
    if (tv) { const [a, b] = P(tv.x, tv.y); g.strokeStyle = COL.yellow; g.lineWidth = 1.5; g.setLineDash([4, 3]); g.beginPath(); g.arc(a, b, CHALLENGES.tail.rules!.loseRange / vw * w, 0, Math.PI * 2); g.stroke(); g.setLineDash([]); }
    for (const v of s.v) if (v.f & 1) dot(v.x, v.y, COL.siren[Math.floor(Date.now() / 200) % 2], 1.5 * u);
    for (const n of s.n) if (n.k !== 'ped' && (n.st === 2 || n.k === 'drone')) dot(n.x, n.y, COL.red, 1 * u);
    for (const b of s.b ?? []) dot(b.x, b.y, Math.floor(Date.now() / 300) % 2 ? COL.red : COL.yellow, 3.5 * u);
    for (const p of s.p) dot(p.x, p.y, p.id === me.id ? COL.white : COL.teal, 1.8 * u);
    for (const pg of s.pg) dot(pg.x, pg.y, pg.k === 'enemy' ? COL.red : COL.yellow, 2 * u);
    if (me.ph === 2) { /* no lkp leak */ }
    if (o.objective) { const [a, b] = P(o.objective.x, o.objective.y); g.strokeStyle = COL.yellow; g.lineWidth = 2; g.strokeRect(a - 3 * u, b - 3 * u, 6 * u, 6 * u); }
    const gt = (o.goal as { target: { x: number; y: number } | null } | null | undefined)?.target;
    if (gt) { // next-goal pointer: a pulsing ring on the nearest fixer kiosk
      const [a0, b0] = P(gt.x, gt.y), ph = (Date.now() % 1000) / 1000; // clamped to the map edge when it is out of the window
      const a = Math.max(x + 4 * u, Math.min(x + w - 4 * u, a0)), b = Math.max(y + 4 * u, Math.min(y + h - 4 * u, b0));
      g.strokeStyle = COL.yellow; g.lineWidth = 1.5; g.globalAlpha = 1 - ph; g.strokeRect(a - (3 + 6 * ph) * u, b - (3 + 6 * ph) * u, (6 + 12 * ph) * u, (6 + 12 * ph) * u);
      g.globalAlpha = 1; g.strokeRect(a - 3 * u, b - 3 * u, 6 * u, 6 * u);
    }
    g.restore();
    g.strokeStyle = COLOR.interactive + '80'; g.strokeRect(x - 1.5, y - 1.5, w + 3, h + 3);
  }
}
